// ConnectToStripe.js
// Form.io Custom Component: Stripe Connect (OAuth) starten
//
// Ziel:
// - Stripe Connect IMMER in neuem Tab öffnen (nie im gleichen Tab)
// - robust gegen Policies, bei denen window.open(...) NULL zurückgibt, obwohl ein Tab aufgeht
//   -> deshalb KEIN window.location.href Fallback mehr
//
// Debug:
// - callId Counter
// - Logs: LISTENER, CLICK START, CONNECT URL, CONNECT RESPONSE, OPEN NEW TAB, ERROR
//
// Hinweise:
// - Kein pointerdown/about:blank mehr (führt bei dir nur zu leeren Tabs / Null-Handles)
// - Nur EIN window.open Versuch pro Klick (nach erfolgreichem Backend-Call)
// - Wenn window.open null -> Hinweis, aber keine Navigation im gleichen Tab

import { Components } from "formiojs";
import config from "@/config/config.js";

// Globaler Debug Counter (browserweit)
if (!window.__stripeConnectDebugCounter) {
  window.__stripeConnectDebugCounter = 0;
}

export default class ConnectToStripe extends Components.components.button {
  static schema(...extend) {
    return super.schema(
      {
        type: "connecttostripe",
        label: "Mit Stripe verbinden",
        key: "connectToStripe",
        size: "md",
        block: false,
        action: "event",
        theme: "primary",
        event: "connectToStripe",

        // Reiner Action-Button
        input: false,
        tableView: false,
        disableOnInvalid: true,

        // optional:
        backendEndpoint: "",
        successUrl: "",
        clientIdKey: "stripeClientId",
        jwtStorageKey: "ozgJwt",
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "ConnectToStripe",
      group: "basic",
      icon: "link",
      weight: 70,
      schema: ConnectToStripe.schema(),
    };
  }

  static editForm() {
    const editForm = super.editForm();

    try {
      const tabs = editForm.components.find((c) => c.key === "tabs");
      const basic = tabs && tabs.components && tabs.components.find((c) => c.key === "basic");

      if (basic && basic.components) {
        basic.components.push(
          {
            type: "textfield",
            key: "backendEndpoint",
            label: "Backend Endpoint (optional)",
            placeholder: "z.B. /api/stripe/connect-url oder leer lassen",
            weight: 30,
          },
          {
            type: "textfield",
            key: "clientIdKey",
            label: "Submission-Key der Stripe Client ID (optional)",
            placeholder: "z.B. stripeClientId",
            weight: 31,
          },
          {
            type: "textfield",
            key: "successUrl",
            label: "ReturnUrl nach Stripe (optional)",
            placeholder: "leer = aktuelle URL",
            weight: 32,
          },
          {
            type: "textfield",
            key: "jwtStorageKey",
            label: "LocalStorage Key für JWT (optional)",
            placeholder: "z.B. ozgJwt",
            weight: 33,
          }
        );
      }
    } catch (_) {}

    return editForm;
  }

  attach(element) {
    const result = super.attach(element);
    const button = element && element.querySelector && element.querySelector("button");
    if (!button) return result;
    button.dataset.paymentPackageBlocked = String(this.component.paymentPackageBlocked === true);
    button.dataset.paymentOriginallyDisabled = String(this.component.disabled === true);
    if (this.component.paymentPackageBlocked || this.component.disabled) {
      button.disabled = true;
      return result;
    }

    // Debug: prüfen ob attach mehrfach kommt / Listener doppelt registriert wird
    if (!button.__stripeListenerAttached) {
      button.__stripeListenerAttached = true;
      console.log("[StripeConnect] Listener attached", button);
    } else {
      console.warn("[StripeConnect] Listener already attached!", button);
    }

    const getApiBase = () => {
      const api = (config && (config.API_URL || config.apiUrl)) || "";
      if (!api) return "/";
      return api.endsWith("/") ? api : api + "/";
    };

    const resolveEndpoint = (endpointRaw) => {
      const apiBase = getApiBase();

      // Default: /api/stripe/connect-url (liefert JSON {url})
      if (!endpointRaw || !String(endpointRaw).trim()) {
        return `${apiBase}api/stripe/connect-url`;
      }

      const ep = String(endpointRaw).trim();

      // Vollständige URL
      if (/^https?:\/\//i.test(ep)) return ep;

      // Absolute Pfadangabe ab Host (z.B. /api/stripe/connect-url)
      if (ep.startsWith("/")) {
        const u = new URL(apiBase);
        return `${u.origin}${ep}`;
      }

      // Relativ zur API Base
      return `${apiBase}${ep}`;
    };

    const jwtExpired = (token) => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return true;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        const exp = payload && payload.exp;
        if (!exp) return true;
        const now = Math.floor(Date.now() / 1000);
        return exp <= now + 30;
      } catch {
        return true;
      }
    };

    // holt (oder erneuert) ein JWT über authenticateBySession
    const ensureJwt = async (apiBase, jwtKey) => {
      let t = localStorage.getItem(jwtKey);
      if (!t || jwtExpired(t)) {
        const url = `${apiBase}api/authenticateBySession`;

        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Username: "session", ApplicationKey: "OZG-MANAGER" }),
        });

        const raw = await res.text();
        const token = raw.replace(/^"|"$/g, "").trim();

        if (!token || token.split(".").length !== 3) {
          throw new Error("Konnte JWT nicht ermitteln (authenticateBySession).");
        }

        localStorage.setItem(jwtKey, token);
        t = token;
      }
      return t;
    };

    const getCommunityIdentifierFromSystem = () => {
      // App.vue setzt window.currentUser und window.communityData beim Initialisieren
      const a = window.currentUser && window.currentUser.CommunityIdentifier;
      if (a != null && String(a).trim()) return String(a).trim();

      const b = window.communityData && window.communityData.communityIdentifier;
      if (b != null && String(b).trim()) return String(b).trim();

      return "";
    };

    button.addEventListener("click", async (e) => {
      const callId = ++window.__stripeConnectDebugCounter;

      console.log("[StripeConnect][CLICK START]", {
        callId,
        time: new Date().toISOString(),
        eventType: e && e.type,
        isTrusted: e && e.isTrusted,
        target: e && e.target,
        currentTarget: e && e.currentTarget,
      });

      if (e && typeof e.preventDefault === "function") e.preventDefault();
      if (e && typeof e.stopPropagation === "function") e.stopPropagation();

      try {
        const apiBase = getApiBase();
        const endpoint = resolveEndpoint(this.component && this.component.backendEndpoint);


        // CommunityIdentifier aus System-Kontext
        const communityIdentifier = getCommunityIdentifierFromSystem();
        if (!communityIdentifier) {
          alert("CommunityIdentifier konnte nicht ermittelt werden (System-Kontext fehlt).");
          return false;
        }

        // ReturnUrl
        let returnUrl =
          this.component && this.component.successUrl && String(this.component.successUrl).trim()
            ? String(this.component.successUrl).trim()
            : window.location.href;

        // Debug nur aktivieren, wenn aktuelle Seite stripeDebug=1 hat
        const debugEnabled = new URL(window.location.href).searchParams.get("stripeDebug") === "1";
        if (debugEnabled) {
          const u = new URL(returnUrl, window.location.origin);
          u.searchParams.set("stripeDebug", "1");
          returnUrl = u.toString();
        }

        const sep = endpoint.includes("?") ? "&" : "?";
        const connectUrl =
          `${endpoint}${sep}` +
          `returnUrl=${encodeURIComponent(returnUrl)}` +
          `&communityIdentifier=${encodeURIComponent(communityIdentifier)}`;

        console.log("[StripeConnect][CONNECT URL]", {
          callId,
          endpoint,
          connectUrl,
          returnUrl,
          communityIdentifier,
        });

        // JWT (Backend ist [Authorize])
        const jwtKey = (this.component && this.component.jwtStorageKey) || "ozgJwt";
        const jwt = await ensureJwt(apiBase, jwtKey);

        // Call an Connect-Endpunkt
        const res = await fetch(connectUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${jwt}` },
        });

        let data = null;
        try {
          data = await res.json();
        } catch {
          // kein JSON
        }

        console.log("[StripeConnect][CONNECT RESPONSE]", {
          callId,
          ok: res.ok,
          status: res.status,
          hasData: !!data,
          hasUrl: !!(data && data.url),
        });

        if (!res.ok || !data || !data.url) {
          const txt = data ? JSON.stringify(data) : await res.text();
          throw new Error(`Connect fehlgeschlagen (HTTP ${res.status}): ${txt}`);
        }

        // Hinweis vor der Weiterleitung zu Stripe.
        // Wenn App.vue/AppVueSystem die BootstrapVue-Funktion bereitstellt,
        // wird ein echtes zentriertes Modal angezeigt.
        // Falls die Funktion nicht verfügbar ist, gibt es einen sicheren Fallback auf confirm(...).
        const alreadyConnected =
          window.__stripeDesiredBtnLabel &&
          String(window.__stripeDesiredBtnLabel).toLowerCase().indexOf("erneut") >= 0;

        let ok = false;

        if (typeof window.__ozgStripeConnectConfirm === "function") {
          ok = await window.__ozgStripeConnectConfirm(alreadyConnected);
        } else {
          const fallbackText = alreadyConnected
            ? "Es besteht bereits eine Stripe-Verbindung für diese Gemeinde.\n\nWenn Sie fortfahren, wird die Gemeinde erneut mit Stripe verbunden.\n\nJetzt zu Stripe wechseln?"
            : "Sie werden jetzt in einem neuen Tab zu Stripe weitergeleitet.\n\nJetzt zu Stripe wechseln?";

          ok = confirm(fallbackText);
        }

        if (!ok) {
          console.log("[StripeConnect][CANCELLED BY USER]", { callId });
          return false;
        }

        // EINZIGE Öffnungsstelle (nie same-tab!)
        window.open(data.url, "_blank", "noopener,noreferrer");
        return false;

      } catch (err) {
        console.log("[StripeConnect][ERROR]", { callId, err });
        console.error("[ConnectToStripe]", err);
        alert("Fehler beim Starten der Stripe-Verbindung: " + (err && err.message ? err.message : err));
        return false;
      }
    });

    return result;
  }
}

Components.addComponent("connecttostripe", ConnectToStripe);
